import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LngLat, Map as MapGL, NavigationControl } from "react-map-gl/mapbox";
import { Layer as DeckLayer } from "@deck.gl/core";
import { MapEvent } from "mapbox-gl";

import {
  api,
  ModelMapParameterMapLayerConfiguration,
  ModelMapWidgetConfiguration,
  ParametersModeledGriddedParameter,
  ParametersModeledMapLayer,
} from "@/lib/api";
import { unwrapAxiosData } from "@/lib/api/query-utils";
import { mapRegionPresetNl } from "@/config/map-region-presets";
import { LayerConfig } from "@/lib/map-layers/layer-state-machine";
import { MapStateMachineInput } from "@/lib/map-layers/map-state-machine";
import { useMapLayers } from "@/lib/map-layers";
import { TimeRangeDimensionValue } from "@/lib/dimensions/time-range-dimension";
import { resolveBaseLayerToMapStyleUrl } from "@/lib/mapbox/base-layer-styles";
import { useRefreshAction } from "@/features/dashboard/features/refresh-timer/hooks";
import { useSelectedTimeRange } from "@/features/dashboard/store";
import useMapViewState from "@/features/shared/widgets/map/hooks/useMapViewState";
import MapControlPositioner from "@/components/map/map-control-positioner";
import MapMarker from "@/components/map/map-marker";
import MapControlTimeseries from "@/components/map/map-control-timeseries";
import Player from "@/features/shared/widgets/map/controls/player-control";
import TimeseriesOverlay from "@/features/dashboard/components/timeseries/timeseries-overlay";
import { DeckGLOverlay } from "@/components/deckgl/deckgl-overlay";
import { toTimeSeriesParameterOption } from "@/features/shared/widgets/map/time-series/time-series-parameter-option.utils";

import { Parameters_ModeledMapLayer } from "./layer";
import { makeTimeSeriesConfigs } from "./make-time-series-configs";
import ModelMapLayerControl from "./model-map-layer-control";
import type { StaticOverlayUiById } from "./model-map-static-overlays-panel";
import { useModelMapStaticOverlayEntries } from "./use-model-map-static-overlays";
import "@/features/shared/widgets/map/map.css";

/** Default map-body height on the dashboard: fills the widget card minus its
 *  ~76px chrome (header). Hosts without that chrome (e.g. the CMS preview) pass
 *  their own value (e.g. `100cqh`). */
const DASHBOARD_MAP_BODY_HEIGHT = "calc(100cqh - 76px)";

interface ModelMapWidgetProps {
  configuration: ModelMapWidgetConfiguration;
  /** CSS height for the map body; defaults to the dashboard card height. */
  containerHeight?: string;
}

type ConfiguredMapLayer = {
  modelLayer: ParametersModeledMapLayer;
  widgetConfig: ModelMapParameterMapLayerConfiguration;
};

interface ModelMapWidgetInnerProps {
  configuration: ModelMapWidgetConfiguration;
  configuredLayers: ConfiguredMapLayer[];
  configuredParameters: ParametersModeledGriddedParameter[];
  containerHeight?: string;
}

type ModelMapLayersBodyProps = {
  configuration: ModelMapWidgetConfiguration;
  mapLayerInput: MapStateMachineInput;
  timeSeriesConfigs: ReturnType<typeof makeTimeSeriesConfigs>;
  containerHeight?: string;
};

export default function ModelMapWrapper({
  configuration,
  containerHeight,
}: ModelMapWidgetProps) {
  const staticOverlaysForFetch = configuration.static_map_overlay_configurations;

  const layersData = useQuery({
    queryKey: ["models", configuration.model_id, "layers"],
    queryFn: () =>
      unwrapAxiosData(api.parameters.listMapLayersForModel(configuration.model_id)),
  });

  const configuredLayers = useMemo(() => {
    if (!layersData.data) return [];
    return (configuration.parameter_map_layer_configurations ?? [])
      .map((widgetConfig) => {
        const modelLayer = layersData.data.find(
          (layer) => layer.id === widgetConfig.parameter_map_layer_id,
        );
        return modelLayer ? { modelLayer, widgetConfig } : null;
      })
      .filter((item): item is ConfiguredMapLayer => !!item);
  }, [layersData.data, configuration]);

  const hasTimeSeriesConfiguration =
    (configuration.parameter_time_series_configurations?.length ?? 0) > 0;

  const parametersData = useQuery({
    queryKey: ["models", configuration.model_id, "parameters"],
    queryFn: () =>
      unwrapAxiosData(api.parameters.listParametersForModel(configuration.model_id)),
    enabled: hasTimeSeriesConfiguration,
  });

  const configuredParameters = useMemo(() => {
    if (!parametersData.data) return [];
    const paramIds = new Set(
      (configuration.parameter_time_series_configurations ?? []).flatMap((c) =>
        c.series.map((s) => s.parameter_id),
      ),
    );
    return parametersData.data.filter((p) => paramIds.has(p.id));
  }, [parametersData.data, configuration]);

  const wantsParameterLayers =
    (configuration.parameter_map_layer_configurations?.length ?? 0) > 0;
  const layersStillResolving = layersData.isPending || layersData.isFetching;
  const hasModelLayers = configuredLayers.length > 0;
  const hasStaticLayers = staticOverlaysForFetch.length > 0;

  /* Only defer rendering while parameter layers the widget actually configured are still
   * resolving (prevents remounting the map when the resolved layer set changes from [] to ids).
   * When nothing is configured (empty widget), fall through and render the base map. */
  const awaitingConfiguredLayers =
    !hasModelLayers && !hasStaticLayers && wantsParameterLayers && layersStillResolving;

  if (awaitingConfiguredLayers) {
    return null;
  }

  return (
    <ModelMap
      configuration={configuration}
      configuredLayers={configuredLayers}
      configuredParameters={configuredParameters}
      containerHeight={containerHeight}
    />
  );
}

export function ModelMap({
  configuration,
  configuredLayers,
  configuredParameters,
  containerHeight,
}: ModelMapWidgetInnerProps) {
  const mapLayerConfigs = useMemo<LayerConfig[]>(
    () =>
      configuredLayers.map(({ modelLayer, widgetConfig }, i) => ({
        layer: new Parameters_ModeledMapLayer(modelLayer, widgetConfig),
        isEnabledInitially: i === 0,
      })),
    [configuredLayers],
  );

  const timeSeriesConfigs = useMemo(() => {
    const configs = configuration.parameter_time_series_configurations ?? [];
    const parameterOptions = configuredParameters.map((parameter) =>
      toTimeSeriesParameterOption(parameter),
    );
    return makeTimeSeriesConfigs(configs, parameterOptions, { defaultSeriesType: "model" });
  }, [configuration.parameter_time_series_configurations, configuredParameters]);

  const timeRange = useSelectedTimeRange();
  const mapLayerInput = useMemo(
    () => ({
      layerConfigs: mapLayerConfigs,
      timeRange,
    }),
    [timeRange, mapLayerConfigs],
  );

  /* useMachine keeps the first actor; layer spawns only run from initial input. Remount when
   * the resolved model-layer set changes (e.g. [] while the layers query runs → ids when ready). */
  const mapLayerActorKey = useMemo(
    () =>
      mapLayerConfigs.length === 0
        ? "no-model-layers"
        : mapLayerConfigs.map((c) => c.layer.id).join("|"),
    [mapLayerConfigs],
  );

  return (
    <ModelMapLayersBody
      key={mapLayerActorKey}
      configuration={configuration}
      mapLayerInput={mapLayerInput}
      timeSeriesConfigs={timeSeriesConfigs}
      containerHeight={containerHeight}
    />
  );
}

function ModelMapLayersBody({
  configuration,
  mapLayerInput,
  timeSeriesConfigs,
  containerHeight = DASHBOARD_MAP_BODY_HEIGHT,
}: ModelMapLayersBodyProps) {
  const mapLayers = useMapLayers(mapLayerInput);

  const staticOverlayConfigs = useMemo(
    () => configuration.static_map_overlay_configurations,
    [configuration.static_map_overlay_configurations],
  );
  const staticOverlayEntries = useModelMapStaticOverlayEntries(staticOverlayConfigs);

  const [staticOverlayUiById, setStaticOverlayUiById] = useState<StaticOverlayUiById>({});

  useEffect(() => {
    if (staticOverlayEntries.length === 0) {
      setStaticOverlayUiById((prev) => (Object.keys(prev).length > 0 ? {} : prev));
      return;
    }
    setStaticOverlayUiById((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const e of staticOverlayEntries) {
        if (next[e.instanceId] === undefined) {
          next[e.instanceId] = { visible: e.enabledByDefault };
          changed = true;
        }
      }
      for (const k of Object.keys(next)) {
        if (!staticOverlayEntries.some((e) => e.instanceId === k)) {
          delete next[k];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [staticOverlayEntries]);

  const visibleStaticDeckLayers = useMemo((): DeckLayer[] => {
    const layers: DeckLayer[] = [];
    for (const e of staticOverlayEntries) {
      const visible = staticOverlayUiById[e.instanceId]?.visible ?? e.enabledByDefault;
      /* Keep a Deck layer in the stack for every loaded overlay and drive visibility with `visible`
       * (clone), instead of splicing instances out/in — MapboxOverlay does not reliably reattach
       * the same layer object after removal. */
      if (e.layer != null) layers.push(e.layer.clone({ visible }));
    }
    return layers;
  }, [staticOverlayEntries, staticOverlayUiById]);

  const setStaticOverlayVisible = useCallback((instanceId: string, visible: boolean) => {
    setStaticOverlayUiById((prev) => ({
      ...prev,
      [instanceId]: { ...prev[instanceId], visible },
    }));
  }, []);

  const timeSeriesEnabled = timeSeriesConfigs.length > 0;
  const timeSeriesEnabledRef = useRef(timeSeriesEnabled);
  timeSeriesEnabledRef.current = timeSeriesEnabled;

  // Note: static layers do not refresh
  useRefreshAction(mapLayers.refresh);

  const [expandedTimeseries, setExpandedTimeseries] = useState(false);

  const [selectedLayerId, setSelectedLayerId] = useState<string | undefined>(
    () => mapLayers.layers[0]?.id,
  );

  useEffect(() => {
    if (mapLayers.layers.length === 0) return;
    setSelectedLayerId((prev) => {
      if (prev && mapLayers.layers.some((l) => l.id === prev)) return prev;
      return mapLayers.layers[0]?.id;
    });
  }, [mapLayers.layers]);

  const [selectedLatLng, setSelectedLatLng] = useState<LngLat | undefined>(undefined);
  const roundedLatLng = useMemo(
    () =>
      selectedLatLng
        ? ({
            lng: Number(selectedLatLng.lng.toFixed(2)),
            lat: Number(selectedLatLng.lat.toFixed(2)),
          } as LngLat)
        : undefined,
    [selectedLatLng],
  );

  // With no map layers there is no time dimension (and no player), so the map
  // never supplies a "time" value. Fall back to "now" for the chart's
  // current-time marker, mirroring the time-series chart configurator preview.
  const fallbackCurrentTime = useMemo(() => new TimeRangeDimensionValue(new Date()), []);

  const initialCoords = useMemo(() => {
    const b = configuration.initial_viewport_bounds;
    if (b) {
      return [
        { lat: b.south_latitude, lng: b.west_longitude },
        { lat: b.north_latitude, lng: b.east_longitude },
      ];
    }
    return mapRegionPresetNl.toBaseLayerCoords();
  }, [configuration.initial_viewport_bounds]);
  const mapStyleUrl = useMemo(
    () => resolveBaseLayerToMapStyleUrl(configuration.base_layer_style),
    [configuration.base_layer_style],
  );
  const mapTileAccessToken = import.meta.env.VITE_MAP_ACCESS_TOKEN;
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const { mapViewState } = useMapViewState({
    containerRef: mapContainerRef,
    initialCoords,
  });

  useEffect(() => {
    if (!timeSeriesEnabled) {
      setSelectedLatLng(undefined);
      setExpandedTimeseries(false);
    }
  }, [timeSeriesEnabled]);

  const onMapLoad = useCallback((e: MapEvent) => {
    e.target.addInteraction("click", {
      type: "click",
      handler: (ev) => {
        if (!timeSeriesEnabledRef.current) return;
        setSelectedLatLng(ev.lngLat);
      },
    });
  }, []);

  const updateSelectedLayer = useCallback(
    (id: string) => {
      setSelectedLayerId(id);

      mapLayers.layers.forEach((layer) => {
        layer.setIsEnabled(layer.id === id);
      });
    },
    [setSelectedLayerId, mapLayers.layers],
  );

  const shouldShowLayerControl = useMemo(() => {
    return !roundedLatLng || !expandedTimeseries;
  }, [expandedTimeseries, roundedLatLng]);

  const staticOverlayRowCount = configuration.static_map_overlay_configurations.length;
  const hasLayerControlTargets = mapLayers.layers.length > 0 || staticOverlayRowCount > 0;

  return (
    <div>
      <div style={{ height: containerHeight }} ref={mapContainerRef}>
        {mapViewState && (
          <MapGL
            initialViewState={mapViewState}
            attributionControl={false}
            maxPitch={0}
            bearing={0}
            bearingSnap={360}
            mapboxAccessToken={mapTileAccessToken}
            mapStyle={mapStyleUrl}
            projection={{ name: "mercator" }}
            onLoad={onMapLoad}
          >
            <DeckGLOverlay
              controller
              interleaved
              getCursor={
                timeSeriesEnabled
                  ? () => "pointer"
                  : ({ isDragging }) => (isDragging ? "grabbing" : "grab")
              }
              layers={[
                ...mapLayers.layers.map((it) => it.renderedLayer),
                ...visibleStaticDeckLayers,
              ].filter((layer): layer is NonNullable<typeof layer> => layer != null)}
            />

            {timeSeriesEnabled && selectedLatLng && (
              <MapMarker
                latitude={selectedLatLng.lat}
                longitude={selectedLatLng.lng}
                onClick={() => setSelectedLatLng(undefined)}
                active
              >
                Selected location
              </MapMarker>
            )}

            <NavigationControl showCompass={false} position="bottom-right" showZoom />

            <Player {...mapLayers} />

            <MapControlPositioner position="top-left">
              {shouldShowLayerControl && hasLayerControlTargets && (
                <ModelMapLayerControl
                  mapLayers={mapLayers}
                  selectedLayerId={selectedLayerId}
                  updateSelectedLayer={updateSelectedLayer}
                  staticOverlayEntries={staticOverlayEntries}
                  staticOverlayUiById={staticOverlayUiById}
                  onSetStaticOverlayVisible={setStaticOverlayVisible}
                />
              )}
            </MapControlPositioner>

            {timeSeriesEnabled && roundedLatLng && (
              <MapControlPositioner position="top-right">
                <TimeseriesOverlay
                  expanded={expandedTimeseries}
                  setExpanded={setExpandedTimeseries}
                  latLng={roundedLatLng}
                  onClose={() => setSelectedLatLng(undefined)}
                >
                  <MapControlTimeseries
                    modelId={configuration.model_id}
                    timeSeriesConfigs={timeSeriesConfigs}
                    latlng={roundedLatLng}
                    currentTime={
                      mapLayers.currentDimensionValues["time"] ?? fallbackCurrentTime
                    }
                  />
                </TimeseriesOverlay>
              </MapControlPositioner>
            )}
          </MapGL>
        )}
      </div>
    </div>
  );
}
